import { Injectable, Provider } from '@angular/core';

/** Datos mínimos que la Disp. 954/2025 permite pedir: sin login, sin registración. */
export interface RegretRequest {
  /** Correo usado en la compra. */
  readonly email: string;
  /** Número de pedido. */
  readonly orderNumber: string;
}

/**
 * Resultado de registrar un arrepentimiento.
 * - `registered`: el backend tomó la solicitud; `code` es el identificador del
 *   trámite que la Disp. 954/2025 exige informar dentro de las 24 h.
 * - `manual-fallback`: el registro automático todavía no está disponible; el
 *   consumidor ejerce el derecho por el canal manual (email de atención).
 */
export type RegretOutcome =
  | { readonly kind: 'registered'; readonly code?: string }
  | { readonly kind: 'manual-fallback' };

/**
 * Puerto de registro del arrepentimiento (PUNTO DE INTEGRACIÓN de #472).
 *
 * El flujo completo exigido por la Disp. 954/2025 —tomar la solicitud, generar
 * un código de identificación e informarlo por email dentro de las 24 h— depende
 * del email transaccional del BFF (#329), que hoy NO existe. Esta abstracción es
 * la costura: la UI depende de este puerto, no de un endpoint. Cuando #329 esté,
 * se provee una implementación real (POST al BFF + código) sin tocar la pantalla.
 */
export abstract class RegretRequestGateway {
  abstract submit(request: RegretRequest): Promise<RegretOutcome>;
}

/**
 * Implementación PROVISORIA mientras el email transaccional (#329) no existe.
 * No inventa un código ni simula un envío que no ocurre: devuelve `manual-fallback`
 * para que la página ofrezca el canal manual de atención. Es el marcador del punto
 * de integración, no la solución definitiva.
 */
@Injectable()
export class PendingBackendRegretGateway extends RegretRequestGateway {
  /**
   * @param _request Datos de la solicitud (sin efecto hasta cablear #329).
   * @returns Siempre `manual-fallback`: el registro automático está pendiente.
   */
  async submit(_request: RegretRequest): Promise<RegretOutcome> {
    return { kind: 'manual-fallback' };
  }
}

/**
 * Provee el gateway del arrepentimiento a nivel aplicación. HOY inyecta la impl
 * provisoria {@link PendingBackendRegretGateway} (deriva al canal manual). **Punto
 * de integración #329:** cuando el email transaccional del BFF exista, se reemplaza
 * este `useClass` por la impl real (POST + código) acá, **sin tocar la pantalla** —
 * el componente sólo depende de la abstracción {@link RegretRequestGateway}.
 * @returns El provider del puerto {@link RegretRequestGateway}.
 */
export function provideRegretGateway(): Provider {
  return { provide: RegretRequestGateway, useClass: PendingBackendRegretGateway };
}
